import { memo } from 'react';

import styles from './NetworkGraph.module.scss';
import type { NetworkGraphEdgeProps } from './NetworkGraphEdge';
import { edgeLabelBox } from './utils/networkGraphEdgeUtils';

export type NetworkGraphEdgeLabelProps = Omit<
  NetworkGraphEdgeProps,
  'onClick' | 'onHover'
> & { isHovered?: boolean };

// Painted in a second pass after every edge stroke so a crossing edge can never
// cover another edge's label. Hover is passed in because the hit area that
// detects it lives in NetworkGraphEdge (a sibling group).
const NetworkGraphEdgeLabelComponent = ({
  edge,
  source,
  sourcePosition,
  target,
  targetPosition,
  targetAngleOffset,
  lateralOffset,
  isHovered,
}: NetworkGraphEdgeLabelProps) => {
  if (!edge.label) {
    return null;
  }

  const { x, y, angle, textWidth, textHeight } = edgeLabelBox({
    edge,
    source,
    sourcePosition,
    target,
    targetPosition,
    targetAngleOffset,
    lateralOffset,
  });
  // Selected edges keep two-way arrows but hide the label (hover-only after landing).
  const visible =
    edge.emphasis !== 'selected' && (edge.labelVisible || isHovered);

  return (
    <g
      className={styles.edgeLabelGroup}
      data-network-graph-edge-label-id={edge.id}
      data-visible={visible || undefined}
      // Rotated along its edge so the label mirrors the arrow's angle and
      // neighboring labels diverge like the arrows do instead of stacking.
      transform={`translate(${x} ${y}) rotate(${angle})`}
    >
      <rect
        className={styles.edgeLabelBackground}
        height={textHeight}
        width={textWidth}
        x={-textWidth / 2}
        y={-textHeight / 2}
      />
      <text className={styles.edgeLabel}>{edge.label}</text>
    </g>
  );
};

// Hover re-maps the label layer; unchanged labels keep their geometry/render.
export const NetworkGraphEdgeLabel = memo(NetworkGraphEdgeLabelComponent);
